import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import Footer from "./header and footer/Footer";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../config/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faChalkboardUser,
  faCheckCircle,
  faSpinner,
  faTimes,
  faBookOpen,
  faChevronLeft,
  faChevronRight,
  faFire,
  faFilter
} from "@fortawesome/free-solid-svg-icons";

const COURSES_PER_PAGE = 6;

/** Strip HTML tags and return plain text */
function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

/** Truncate plain text to maxLen characters with ellipsis */
function truncateText(text, maxLen = 120) {
  if (!text) return "";
  const plain = stripHtml(text);
  if (plain.length <= maxLen) return plain;
  return plain.substring(0, maxLen).trimEnd() + "…";
}

function Courses() {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const userId = localStorage.getItem("id");
  const navigate = useNavigate();
  const location = useLocation();
  const [enrolled, setEnrolled] = useState([]);

  // Check URL query parameters for category filter (from Navbar or Footer)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get("category");
    if (categoryParam) {
      setSelectedCategory(categoryParam);
      setCurrentPage(1);
    }
  }, [location.search]);

  // Reset page to 1 whenever category or search filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchTerm]);

  useEffect(() => {
    // Fetch courses from backend
    fetch(`${API_BASE_URL}/api/courses`)
      .then((response) => response.json())
      .then((data) => {
        setCourses(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching courses:", error);
        setLoading(false);
      });

    // Fetch categories created by admin from backend
    fetch(`${API_BASE_URL}/api/categories`)
      .then((response) => response.json())
      .then((data) => {
        setCategories(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
      });

    // Fetch user enrollments if logged in
    if (userId) {
      fetch(`${API_BASE_URL}/api/learning/${userId}`)
        .then((response) => response.json())
        .then((data) => {
          if (Array.isArray(data)) {
            const ids = data.map((c) => Number(c.course_id || c.id));
            setEnrolled(ids);
          }
        })
        .catch((error) => {
          console.error("Error fetching enrolled courses:", error);
        });
    }
  }, [userId]);

  function enrollCourse(courseId) {
    if (!userId) {
      toast.error("Please log in to enroll in a course.");
      navigate("/login");
      return;
    }
    const enrollRequest = {
      userId: Number(userId),
      courseId: Number(courseId)
    };
    axios
      .post(`${API_BASE_URL}/api/learning`, enrollRequest)
      .then(() => {
        const numId = Number(courseId);
        setEnrolled((prev) => [...prev, numId]);
        toast.success("Course Enrolled successfully!", {
          position: "top-right",
          autoClose: 1200
        });
        setTimeout(() => {
          navigate("/learnings");
        }, 1200);
      })
      .catch((error) => {
        console.error("Enrollment error:", error);
        toast.error("Could not enroll in course. Please try again.");
      });
  }

  // Filter courses based on search term and category
  const filteredCourses = courses.filter((course) => {
    const title = (course.courseName || course.course_name || "").toLowerCase();
    const tutor = (course.instructor || course.tutor || "").toLowerCase();
    const desc = stripHtml(course.description || "").toLowerCase();
    const catName = (course.category_name || "").toLowerCase();
    const term = searchTerm.toLowerCase().trim();

    const matchesSearch = !term ||
      title.includes(term) ||
      tutor.includes(term) ||
      desc.includes(term) ||
      catName.includes(term);

    if (selectedCategory === "All") return matchesSearch;

    // Support filtering by category ID or category name
    const matchesCategory =
      Number(course.category_id) === Number(selectedCategory) ||
      (course.category_name && course.category_name.toLowerCase() === selectedCategory.toLowerCase());

    return matchesSearch && matchesCategory;
  });

  // Pagination calculation
  const totalPages = Math.ceil(filteredCourses.length / COURSES_PER_PAGE);
  const startIndex = (currentPage - 1) * COURSES_PER_PAGE;
  const endIndex = startIndex + COURSES_PER_PAGE;
  const paginatedCourses = filteredCourses.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    const section = document.getElementById("courses-catalog-section");
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Trending courses — ONLY up to 6 courses marked as featured/trending by admin in dashboard
  const adminTrendingCourses = courses.filter(
    (c) => c.featured === 1 || Number(c.featured) === 1
  ).slice(0, 6);
  // If admin hasn't selected any courses yet, fallback to first 6 courses
  const trendingCourses = adminTrendingCourses.length > 0
    ? adminTrendingCourses
    : courses.slice(0, 6);

  return (
    <div>
      <Navbar page={"courses"} />

      {/* Header Banner */}
      <div className="page-banner" style={{ padding: "70px 6% 60px" }}>
        <h1>Explore Our Accredited Courses</h1>
        <p style={{ marginBottom: "28px" }}>
          Master in-demand technical skills with expert-led video modules, assessments, and verified certifications.
        </p>

        {/* Integrated Search Bar */}
        <div
          style={{
            maxWidth: "640px",
            margin: "0 auto",
            position: "relative",
            display: "flex",
            alignItems: "center"
          }}
        >
          <FontAwesomeIcon
            icon={faSearch}
            style={{
              position: "absolute",
              left: "20px",
              color: "#818cf8",
              fontSize: "1.15rem",
              zIndex: 2
            }}
          />
          <input
            type="text"
            placeholder="Search by course title, topic, instructor, or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "16px 48px 16px 52px",
              borderRadius: "32px",
              border: "1.5px solid rgba(255,255,255,0.25)",
              background: "rgba(255,255,255,0.14)",
              backdropFilter: "blur(14px)",
              color: "#fff",
              fontSize: "1rem",
              outline: "none",
              boxShadow: "0 8px 30px rgba(0,0,0,0.2)"
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              style={{
                position: "absolute",
                right: "18px",
                background: "rgba(255,255,255,0.2)",
                border: "none",
                borderRadius: "50%",
                width: "28px",
                height: "28px",
                color: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.85rem"
              }}
              title="Clear search"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area — Sidebar + Catalog Grid */}
      <div id="courses-catalog-section" style={{ padding: "50px 6% 20px", maxWidth: "1400px", margin: "0 auto" }}>

        <div className="courses-layout">
          {/* ===== LEFT SIDEBAR: Category Navigation ===== */}
          <aside className="category-sidebar">
            <div className="sidebar-header">
              <FontAwesomeIcon icon={faFilter} style={{ color: "#4f46e5" }} />
              <span>Categories</span>
            </div>

            {/* All Courses */}
            <button
              className={`sidebar-cat-btn ${selectedCategory === "All" ? "active" : ""}`}
              onClick={() => setSelectedCategory("All")}
            >
              <span className="sidebar-cat-name">All Courses</span>
              <span className="sidebar-cat-count">{courses.length}</span>
            </button>

            {/* Admin-created categories */}
            {categories.map((cat) => {
              const catCourseCount = courses.filter((c) =>
                Number(c.category_id) === Number(cat.id) ||
                (c.category_name && c.category_name.toLowerCase() === cat.name.toLowerCase())
              ).length;

              const isCatActive =
                selectedCategory === cat.name ||
                Number(selectedCategory) === Number(cat.id);

              return (
                <button
                  key={cat.id}
                  className={`sidebar-cat-btn ${isCatActive ? "active" : ""}`}
                  onClick={() => setSelectedCategory(cat.name)}
                >
                  <span className="sidebar-cat-name">{cat.name}</span>
                  <span className="sidebar-cat-count">{catCourseCount}</span>
                </button>
              );
            })}
          </aside>

          {/* ===== RIGHT: Course Catalog ===== */}
          <div className="courses-main">
            {/* Search / Filter feedback bar */}
            {(searchTerm.trim() || selectedCategory !== "All") && (
              <div style={{
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: "12px",
                padding: "12px 20px",
                marginBottom: "28px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "10px"
              }}>
                <span style={{ color: "#1e40af", fontSize: "0.92rem", fontWeight: 600 }}>
                  Showing {filteredCourses.length} course{filteredCourses.length === 1 ? "" : "s"}
                  {selectedCategory !== "All" && ` in "${selectedCategory}"`}
                  {searchTerm.trim() && ` matching "${searchTerm}"`}
                </span>
                <button
                  onClick={() => { setSearchTerm(""); setSelectedCategory("All"); }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#2563eb",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    cursor: "pointer"
                  }}
                >
                  Reset Filters
                </button>
              </div>
            )}

            {/* Course Catalog Content */}
            {loading ? (
              <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b" }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "2.2rem", marginBottom: "14px", color: "#2563eb" }} />
                <p>Loading course catalog...</p>
              </div>
            ) : filteredCourses.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b", background: "#f8fafc", borderRadius: "20px", border: "1px solid #e2e8f0" }}>
                <FontAwesomeIcon icon={faBookOpen} style={{ fontSize: "2.5rem", marginBottom: "14px", color: "#94a3b8" }} />
                <h3 style={{ color: "#0f172a", marginBottom: "8px" }}>No courses found</h3>
                <p style={{ margin: "0 0 18px", color: "#64748b" }}>Try adjusting your search query or selecting another category.</p>
                <button
                  onClick={() => { setSearchTerm(""); setSelectedCategory("All"); }}
                  style={{
                    background: "#2563eb",
                    color: "#fff",
                    border: "none",
                    borderRadius: "10px",
                    padding: "10px 20px",
                    fontWeight: 700,
                    cursor: "pointer"
                  }}
                >
                  Show All Courses
                </button>
              </div>
            ) : (
              <div>
                {/* Catalog Grid */}
                <div className="courses-container">
                  {paginatedCourses.map((course) => renderCourseCard(course))}
                </div>

                {/* Pagination Controls */}
                <div className="pagination-container">
                  {totalPages > 1 && (
                    <div className="pagination-controls">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="pagination-btn"
                        aria-label="Previous Page"
                      >
                        <FontAwesomeIcon icon={faChevronLeft} /> Previous
                      </button>

                      <div className="pagination-numbers">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`pagination-num ${currentPage === pageNum ? "active" : ""}`}
                            aria-label={`Page ${pageNum}`}
                          >
                            {pageNum}
                          </button>
                        ))}
                      </div>

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="pagination-btn"
                        aria-label="Next Page"
                      >
                        Next <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                  )}

                  <div className="pagination-info">
                    Showing {startIndex + 1}–{Math.min(endIndex, filteredCourses.length)} of {filteredCourses.length} courses
                    {totalPages > 1 ? ` • Page ${currentPage} of ${totalPages}` : " (All catalog courses shown • 6 per page)"}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== TRENDING COURSES SECTION (Selected by Admin in Dashboard, Max 6) ===== */}
      {trendingCourses.length > 0 && (
        <div style={{
          padding: "50px 6% 70px",
          maxWidth: "1400px",
          margin: "0 auto",
        }}>
          <div className="trending-section">
            {/* Trending Header — category filter removed per user requirement */}
            <div className="trending-header">
              <div>
                <h2 style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontSize: "1.6rem",
                  fontWeight: 800,
                  color: "#0f172a",
                  margin: "0 0 6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px"
                }}>
                  <FontAwesomeIcon icon={faFire} style={{ color: "#ef4444" }} />
                  Trending Courses
                </h2>
                <p style={{ color: "#64748b", fontSize: "0.9rem", margin: 0 }}>
                  Top featured courses selected by the academic faculty (up to 6 trending courses)
                </p>
              </div>
            </div>

            {/* Trending Courses Grid — 2 columns wide */}
            <div className="trending-grid">
              {trendingCourses.map((course) => renderTrendingCard(course))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );

  function renderCourseCard(course) {
    const courseId = Number(course.course_id || course.id);
    const courseTitle = course.courseName || course.course_name || "Course Module";
    const isEnrolled = enrolled.includes(courseId);
    const truncatedDesc = truncateText(course.description, 120);

    return (
      <div key={courseId} className="course-card">
        <div className="course-image-wrapper">
          <img
            src={course.p_link || course.photo || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop&q=60"}
            alt={courseTitle}
            className="course-image"
            onError={(e) => {
              e.target.src = "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop&q=60";
            }}
          />
          {course.category_name && (
            <span className="course-category-badge">
              {course.category_name}
            </span>
          )}
        </div>

        <div className="course-details">
          <h3 className="course-heading">{courseTitle}</h3>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
            <span style={{ fontSize: "0.85rem", color: "#64748b", fontWeight: "600" }}>
              <FontAwesomeIcon icon={faChalkboardUser} style={{ marginRight: "6px", color: "#2563eb" }} />
              {course.instructor || course.tutor || "Faculty Instructor"}
            </span>
            <span style={{ fontSize: "1.1rem", color: "#2563eb", fontWeight: "800" }}>
              ${course.price}
            </span>
          </div>

          {truncatedDesc && (
            <p className="course-description" style={{ color: "#64748b", fontSize: "0.86rem", lineHeight: "1.5" }}>
              {truncatedDesc}
            </p>
          )}
        </div>

        {isEnrolled ? (
          <button
            className="enroll-button"
            style={{ background: "#10b981", color: "#fff" }}
            onClick={() => navigate("/learnings")}
          >
            <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: "6px" }} />
            Enrolled — Go to Learning
          </button>
        ) : (
          <button
            className="enroll-button"
            onClick={() => enrollCourse(courseId)}
          >
            Enroll Now
          </button>
        )}
      </div>
    );
  }

  function renderTrendingCard(course) {
    const courseId = Number(course.course_id || course.id);
    const courseTitle = course.courseName || course.course_name || "Course Module";
    const isEnrolled = enrolled.includes(courseId);
    const truncatedDesc = truncateText(course.description, 100);

    return (
      <div key={`trending-${courseId}`} className="trending-card">
        <img
          src={course.p_link || course.photo || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop&q=60"}
          alt={courseTitle}
          className="trending-card-img"
          onError={(e) => {
            e.target.src = "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop&q=60";
          }}
        />
        <div className="trending-card-content">
          <div>
            {course.category_name && (
              <span className="trending-card-badge">{course.category_name}</span>
            )}
            <h4 className="trending-card-title">{courseTitle}</h4>
            {truncatedDesc && (
              <p className="trending-card-desc">{truncatedDesc}</p>
            )}
            <div className="trending-card-meta">
              <span>
                <FontAwesomeIcon icon={faChalkboardUser} style={{ marginRight: "5px", color: "#4f46e5" }} />
                {course.instructor || course.tutor || "Instructor"}
              </span>
              <span style={{ color: "#2563eb", fontWeight: 800 }}>${course.price}</span>
            </div>
          </div>
          {isEnrolled ? (
            <button
              className="enroll-button"
              style={{ background: "#10b981", color: "#fff", margin: "0" }}
              onClick={() => navigate("/learnings")}
            >
              <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: "6px" }} />
              Enrolled
            </button>
          ) : (
            <button
              className="enroll-button"
              style={{ margin: "0" }}
              onClick={() => enrollCourse(courseId)}
            >
              Enroll Now
            </button>
          )}
        </div>
      </div>
    );
  }
}

export default Courses;
